import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { VersionCheckDebugInfo } from "@/hooks/useVersionCheck";

interface VersionUpdateModalProps {
  open: boolean;
  onDismiss: () => void;
  onReload: () => void;
  debugInfo?: VersionCheckDebugInfo;
}

/** Compact one-line diagnostic, e.g. "was 1556797366304 → now 1556800000111 · 200 text/plain · 14:32:07". */
function formatDebugInfo(info: VersionCheckDebugInfo): string {
  const was = info.initialVersion ?? "?";
  // The "now" slot holds the detected version. If the last poll was
  // rejected (no version on this snapshot), surface the reason
  // explicitly — dropping a token like "bad-content-type" into the
  // version position would read as if the reject reason were a version.
  const now = info.detectedVersion
    ? info.detectedVersion
    : info.lastRejectReason
      ? `rejected (${info.lastRejectReason})`
      : "?";
  const status = info.lastStatus ?? "?";
  const ctype = (info.lastContentType ?? "?").split(";")[0];
  const at = info.lastCheckedAt
    ? new Date(info.lastCheckedAt).toLocaleTimeString()
    : "?";
  return `was ${was} → now ${now} · ${status} ${ctype} · ${at}`;
}

/**
 * Modal dialog that prompts users to reload when a new version is available.
 * Uses AlertDialog to require user interaction (can't be dismissed by clicking outside).
 */
export function VersionUpdateModal({
  open,
  onDismiss,
  onReload,
  debugInfo,
}: VersionUpdateModalProps) {
  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Update Available</AlertDialogTitle>
          <AlertDialogDescription>
            A new version of Sked is available. Reload to get the latest
            features and improvements.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {debugInfo && (
          <p className="text-xs text-muted-foreground font-mono break-all">
            {formatDebugInfo(debugInfo)}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onDismiss}>Later</AlertDialogCancel>
          <AlertDialogAction onClick={onReload}>Reload Now</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
